import { Decorative } from "@faire/design-tokens";
import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { Close } from "@faire/web/slate/icons/foundation/Close";
import { Insider } from "@faire/web/slate/icons/InsiderIcon";
import { Row } from "@faire/web/slate/Layout";
import { GrowingSpacer, Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import isBefore from "date-fns/isBefore";
import React, { useState } from "react";
import styled from "styled-components";

import { useInsiderState } from "@faire/retailer/hooks/insider/useInsiderState";
import { useSettingInsiderPartnershipLaunchAnnouncementsEnabled } from "@faire/retailer/settings/useSettingInsiderPartnershipLaunchAnnouncementsEnabled";
import { useMilestone } from "@faire/retailer/stores/domain/useMilestone";

const BANNER_MAX_VIEW_COUNT = 3;
const END_OF_SEPTEMBER = new Date(Date.UTC(2024, 8, 30, 23, 59, 59));

export const InsiderExpandedCoverageBanner: React.FC = () => {
  const isLaunchAnnouncementsSettingEnabled =
    useSettingInsiderPartnershipLaunchAnnouncementsEnabled();
  const { isInsiderActive, isInsiderAutoEnrollTrial, isInsiderTrial } =
    useInsiderState();

  const { count: bannerSeenCount, incrementCount: incrementBannerSeenCount } =
    useMilestone("INSIDER_EXPANDED_FREE_SHIPPING_BANNER_SEEN");
  const { hasOccurred: hasBannerBeenDismissed, setHasOccurred: dismissBanner } =
    useMilestone("INSIDER_EXPANDED_FREE_SHIPPING_BANNER_DISMISS");

  const [isBannerVisible, setBannerVisibility] = useState(true);

  const viewRef = useDoActionWhenVisible(() => {
    incrementBannerSeenCount();
  });

  const isBeforeEndOfSeptember = isBefore(Date.now(), END_OF_SEPTEMBER);

  const showExpandedCoverageBanner =
    isLaunchAnnouncementsSettingEnabled &&
    isInsiderActive &&
    !isInsiderAutoEnrollTrial &&
    !isInsiderTrial &&
    !hasBannerBeenDismissed &&
    isBeforeEndOfSeptember &&
    bannerSeenCount <= BANNER_MAX_VIEW_COUNT &&
    isBannerVisible;

  if (!showExpandedCoverageBanner) {
    return null;
  }

  return (
    <BannerWrapper ref={viewRef}>
      <InsiderIcon />
      <Spacer size="2x" />
      <Typography data-test-id="insider-expanded-coverage-content">
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Insiders now get free shipping on twice the number of brands!</MediumFormatting> Filter by Insider to shop."
          description={{
            text: "Banner message to inform retailer of expanded Insider coverage",
            img: "https://cdn.faire.com/fastly/72ff5c883836f904e537d38e1964838e132ea0325764d4868c389c351e989177.png",
          }}
          values={{
            MediumFormatting,
          }}
        />
      </Typography>
      <GrowingSpacer base="2x" />
      <CloseIcon
        fontSize="16px"
        onClick={() => {
          setBannerVisibility(false);
          dismissBanner();
        }}
      />
    </BannerWrapper>
  );
};

const BannerWrapper = styled(Row).attrs({
  align: { mobileAndAbove: "flex-start", tabletAndAbove: "center" },
})`
  background-color: ${Decorative.tint.teal};
  margin: ${getSpacing("1x", 0)};
  padding: ${getSpacing(12, "2x")};
`;

const InsiderIcon = styled(Insider).attrs({
  fontSize: "16px",
  color: Decorative.icon.teal,
  fill: Decorative.icon.teal,
  small: true,
})`
  padding: ${getSpacing("1/2x", 0, 0, 0)};
  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 0;
  }
`;

const CloseIcon = styled(Close).attrs({ fontSize: "16px" })`
  cursor: pointer;
  padding: ${getSpacing("1/2x", 0, 0, 0)};
  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 0;
  }
`;
